/* ====================================================================
   LAAGAM · "View similar" popup — shared engine
   --------------------------------------------------------------------
   Port of the PDP sold-out suggestions popup (snippets/sold-out-
   suggestions.liquid, display mode "popup") as a page-level component
   any surface can open — currently the lookbook cards (hotspots and the
   "View products" drawer).

   Same markup, same classes, same product cascade, same desktop modal /
   mobile bottom-sheet behaviour.

   Product cascade (first match wins), identical to the PDP:
     1. Hand-picked `custom.soldout_suggestions` handles (passed in via
        the registry). When present they ALWAYS win and are never dropped
        for lacking the requested size.
     2. Exact product type + same swatch colour.
     3. Exact product type, other colours.
     4. Near product type (the type search is a text match), same colour
        first.
     5. Shopify product recommendations.
   Swatch colour isn't exposed by /products/{handle}.js, so each
   candidate's colour is read from the `?view=xsell-color` product view.

   Public API
     LaagamXsell.register({ handle: { id, type, color, suggestions[] } })
     LaagamXsell.open({ handle, size, heading })
     LaagamXsell.similar({ handle, limit }) → Promise<[{ url, title, image, price, … }]>
     LaagamXsell.close()
   Or declaratively, on any clickable element:
     data-xsim-open data-xsim-handle="…" [data-xsim-size="M"]
     [data-xsim-heading="…"]
   ==================================================================== */

(function () {
  if (window.LaagamXsell) return;

  var CFG = window.LaagamXsellConfig || {};
  var HEADING = CFG.heading || 'SIMILAR PRODUCTS';
  var ADD_LABEL = CFG.addLabel && !/translation missing/i.test(CFG.addLabel) ? CFG.addLabel : 'Add to cart';
  var SUCCESS_TEXT = CFG.successText || 'Added to cart';
  var ERROR_TEXT = CFG.errorText || 'Something went wrong.';
  var MONEY_FORMAT = CFG.moneyFormat || '';
  var MONEY_FORMAT_WC = CFG.moneyWithCurrencyFormat || '';
  var USE_CODE = CFG.currencyCodeEnabled === true || String(CFG.currencyCodeEnabled) === 'true';
  var FALLBACK_CCY = CFG.currency || 'EUR';
  var USE_SWATCH = CFG.useSwatchImage !== false;
  var MAX_ITEMS = parseInt(CFG.limit || 20, 10);
  var SHOW_PRICE = CFG.showPrice !== false;
  var ADD_ICON_URL =
    CFG.addIconUrl ||
    'https://cdn.shopify.com/s/files/1/0720/5630/7997/files/add_24dp_000000_FILL0_wght200_GRAD0_opsz24_3.svg?v=1757274950';
  var DESKTOP_MIN = 750;

  /* Handle → { id, type, color, suggestions[] }, filled server-side by
     whichever section renders the trigger. Unknown handles still work:
     the data is derived from the product JSON / xsell-color view. */
  var registry = {};

  /* Sections publish their product data as
     <script type="application/json" data-xsim-registry>…</script>.
     They are inline (so they parse before this deferred script) and can be
     added later by the theme editor, hence the re-scan on every open. */
  function collectRegistryTags() {
    document.querySelectorAll('script[data-xsim-registry]:not([data-xsim-parsed])').forEach(function (tag) {
      tag.setAttribute('data-xsim-parsed', '');
      try {
        registerMap(JSON.parse(tag.textContent));
      } catch (e) {}
    });
  }

  /* ── Helpers ─────────────────────────────────────────────────────── */
  function isDesktop() { return window.innerWidth >= DESKTOP_MIN; }
  function shopRoot() {
    try {
      return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    } catch (e) {
      return '/';
    }
  }
  function normColor(c) { return String(c == null ? '' : c).trim().toLowerCase(); }
  function normType(t) { return String(t == null ? '' : t).trim().toLowerCase(); }
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (m) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[m];
    });
  }
  function money(cents) {
    try {
      var fmt = USE_CODE ? MONEY_FORMAT_WC || MONEY_FORMAT || null : MONEY_FORMAT || MONEY_FORMAT_WC || null;
      if (window.Shopify && typeof Shopify.formatMoney === 'function') {
        if (fmt) return Shopify.formatMoney(cents, fmt);
        return Shopify.formatMoney(cents);
      }
    } catch (e) {}
    try {
      return ((Number(cents) || 0) / 100).toLocaleString(undefined, { style: 'currency', currency: FALLBACK_CCY });
    } catch (e) {
      return (cents / 100).toFixed(2);
    }
  }
  function splitTitleParts(title) {
    var parts = String(title || '').split(' - ');
    var main = (parts.shift() || '').trim();
    var sub = parts.join(' - ').trim();
    return { main: main, sub: sub };
  }
  /* Same convention as "Complete the Look": a product image whose alt text
     is exactly "swatch" (or "swatch-hide") is used as its thumbnail. */
  function findSwatchImageUrl(pj) {
    if (!Array.isArray(pj.media)) return null;
    var hit = pj.media.find(function (m) {
      if (m.media_type !== 'image') return false;
      var a = (m.alt || '').toLowerCase().trim();
      return a === 'swatch' || a === 'swatch-hide';
    });
    if (!hit) return null;
    return (hit.preview_image && hit.preview_image.src) || hit.src || null;
  }
  async function concurrentMap(items, limit, worker) {
    var results = new Array(items.length);
    var i = 0;
    var workers = new Array(Math.min(limit, items.length)).fill(0).map(async function () {
      while (i < items.length) {
        var idx = i++;
        results[idx] = await worker(items[idx], idx).catch(function () { return null; });
      }
    });
    await Promise.all(workers);
    return results;
  }

  /* ── Caches ──────────────────────────────────────────────────────── */
  var productCache = new Map();
  var colorCache = new Map();
  var typePoolCache = new Map();
  var recPoolCache = new Map();

  async function fetchProductJsonCached(handle) {
    if (productCache.has(handle)) return productCache.get(handle);
    var res = await fetch(shopRoot() + 'products/' + handle + '.js', { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error('product json');
    var pj = await res.json();
    productCache.set(handle, pj);
    return pj;
  }
  /* Swatch colour, from the lightweight `?view=xsell-color` product view
     (metafields aren't available on /products/{handle}.js). */
  async function fetchColorCached(handle) {
    if (colorCache.has(handle)) return colorCache.get(handle);
    var color = '';
    try {
      var res = await fetch(shopRoot() + 'products/' + handle + '?view=xsell-color', { headers: { Accept: 'text/html' } });
      var text = res.ok ? await res.text() : '';
      var m = text.match(/XSC::([\s\S]*?)::XSC/);
      color = m ? normColor(m[1]) : '';
    } catch (e) {}
    colorCache.set(handle, color);
    return color;
  }

  /* ── Size matching (ported verbatim from the PDP snippet) ────────── */
  function normalizeSizeString(s) {
    return String(s || '')
      .toLowerCase()
      .replace(/[\n_-]/g, '')
      .replace(/eur|europe|eu|us|uk|talla|size/g, '')
      .replace(/[^a-z0-9./]/g, '');
  }
  function extractSizeNumber(s) {
    var str = String(s || '').toLowerCase();
    var frac = str.match(/(\d+(?:[.,]\d+)?)\s*([12])\/3/);
    if (frac) {
      var base = parseFloat(frac[1].replace(',', '.'));
      var add = frac[2] === '1' ? 1 / 3 : 2 / 3;
      return base + add;
    }
    var m = str.match(/(\d+(?:[.,]\d+)?)/);
    return m ? parseFloat(m[1].replace(',', '.')) : null;
  }
  function extractLetterSize(s) {
    var m = String(s || '').toLowerCase().match(/xxxl|xxl|xl|xs|s|m|l/);
    return m ? m[0] : null;
  }
  function looseSizeCompare(a, b) {
    if (a == null || b == null) return false;
    var na = normalizeSizeString(a);
    var nb = normalizeSizeString(b);
    if (na && nb) {
      if (na === nb) return true;
      if (na.includes(nb) || nb.includes(na)) return true;
    }
    var an = extractSizeNumber(a);
    var bn = extractSizeNumber(b);
    if (an != null && bn != null && Math.abs(an - bn) < 0.05) return true;
    var la = extractLetterSize(a);
    var lb = extractLetterSize(b);
    return !!(la && lb && la === lb);
  }
  function pickVariantForSizeStrict(pj, size) {
    var wantedRaw = String(size || '').trim();
    if (!wantedRaw) return null;
    var variants = Array.isArray(pj.variants) ? pj.variants : [];
    var match = variants.find(function (v) {
      return (
        v.available &&
        [v.option1, v.option2, v.option3].some(function (val) { return looseSizeCompare(wantedRaw, val); })
      );
    });
    if (match) return match;
    return (
      variants.find(function (v) { return v.available && looseSizeCompare(wantedRaw, v.title); }) || null
    );
  }
  function pickVariantLoose(pj, size) {
    var wanted = String(size || '').toLowerCase();
    if (Array.isArray(pj.variants) && pj.variants.length) {
      if (wanted) {
        var exactAvail = pj.variants.find(function (v) {
          return (
            v.available &&
            [v.option1, v.option2, v.option3].some(function (val) {
              return (val || '').toString().toLowerCase() === wanted;
            })
          );
        });
        if (exactAvail) return exactAvail;
        var titleAvail = pj.variants.find(function (v) {
          return v.available && (v.title || '').toLowerCase().includes(wanted);
        });
        if (titleAvail) return titleAvail;
      }
      return pj.variants.find(function (v) { return v.available; }) || pj.variants[0] || null;
    }
    return null;
  }
  function stockValueForVariant(v) {
    if (!v) return -1;
    var untracked = !v.inventory_management || v.inventory_policy === 'continue';
    if (v.available && untracked) return Number.MAX_SAFE_INTEGER;
    var qty = typeof v.inventory_quantity === 'number' ? v.inventory_quantity : 0;
    return v.available ? qty : -1;
  }

  /* ── Pools ───────────────────────────────────────────────────────── */
  async function poolFromHandles(handles, currentHandle) {
    var list = (handles || []).filter(function (h) { return h && h !== currentHandle; }).slice(0, MAX_ITEMS);
    if (!list.length) return [];
    var prods = await concurrentMap(list, 6, async function (h) {
      try { return await fetchProductJsonCached(h); } catch (e) { return null; }
    });
    return prods.filter(Boolean);
  }
  /* Products sharing this product's type, via the predictive search API.
     `fields=product_type` restricts matching to the product type field.
     The endpoint caps `resources[limit]` at 10. */
  async function poolFromType(productType, currentHandle) {
    if (!productType) return [];
    var key = productType + '|' + currentHandle;
    if (typePoolCache.has(key)) return typePoolCache.get(key);
    var promise = (async function () {
      try {
        var typeLimit = Math.min(MAX_ITEMS, 10);
        var url =
          shopRoot() +
          'search/suggest.json?q=' +
          encodeURIComponent(productType) +
          '&resources[type]=product&resources[limit]=' +
          typeLimit +
          '&resources[options][unavailable_products]=last&resources[options][fields]=product_type';
        var res = await fetch(url, { headers: { Accept: 'application/json' } });
        if (!res.ok) return [];
        var data = await res.json();
        var results = (data.resources && data.resources.results && data.resources.results.products) || [];
        return await poolFromHandles(
          results.map(function (p) { return p.handle; }),
          currentHandle
        );
      } catch (e) {
        return [];
      }
    })();
    typePoolCache.set(key, promise);
    return promise;
  }
  /* Final fallback: Shopify product recommendations. */
  async function poolFromRecommendations(productId, currentHandle) {
    if (!productId) return [];
    if (recPoolCache.has(productId)) return recPoolCache.get(productId);
    var promise = (async function () {
      try {
        var recUrl =
          shopRoot() + 'recommendations/products.json?product_id=' + productId + '&limit=' + MAX_ITEMS + '&intent=related';
        var res = await fetch(recUrl, { headers: { Accept: 'application/json' } });
        if (!res.ok) return [];
        var data = await res.json();
        return await poolFromHandles(
          (data.products || []).map(function (p) { return p.handle; }),
          currentHandle
        );
      } catch (e) {
        return [];
      }
    })();
    recPoolCache.set(productId, promise);
    return promise;
  }

  /* ── Enrichment ──────────────────────────────────────────────────── */
  function buildEnriched(pool, limit, ctx) {
    if (!pool || !pool.length) return [];
    var selectedSize = String(ctx.size || '').trim();
    // Hand-picked suggestions are curated, so never drop them for lacking
    // the size — still prefer the matching-size variant, just don't require it.
    var strictMode = !!selectedSize && !ctx.fromMetafield;
    var enriched = [];
    for (var i = 0; i < pool.length; i++) {
      var pj = pool[i];
      if (!pj || pj.handle === ctx.handle) continue;
      var mv = strictMode ? pickVariantForSizeStrict(pj, selectedSize) : pickVariantLoose(pj, selectedSize);
      if (strictMode) {
        if (mv && mv.available) enriched.push({ productJson: pj, matchedVariant: mv, stock: stockValueForVariant(mv) });
      } else if (mv && (!ctx.requireAvailable || mv.available)) {
        enriched.push({ productJson: pj, matchedVariant: mv, stock: stockValueForVariant(mv) });
      }
      if (limit && enriched.length >= limit) break;
    }
    enriched.sort(function (a, b) { return b.stock - a.stock; });
    return enriched;
  }

  /* Cascade: hand-picked → exact type + same colour → exact type other
     colours → near type (text match) same colour → near type other colours
     → Shopify recs. Each step only tops up the list; recs kick in only when
     the whole type pool has nothing displayable. */
  async function buildCascade(ctx, count) {
    if (ctx.suggestions && ctx.suggestions.length) {
      var picked = await poolFromHandles(ctx.suggestions, ctx.handle);
      var pickedEnriched = buildEnriched(picked, count, ctx);
      if (pickedEnriched.length) return pickedEnriched;
    }

    // Past the hand-picked list the size requirement applies again: an
    // automatic suggestion is only useful if it exists in the size asked for.
    ctx = Object.assign({}, ctx, { fromMetafield: false });

    var pool = await poolFromType(ctx.type, ctx.handle);
    // The type search is a text match ("T-Shirt" also finds "Long Sleeve
    // T-Shirt"), so rank the EXACT type first and let the near matches only
    // fill the gaps. Inside each, same swatch colour goes first.
    var colors = [];
    if (ctx.color && pool.length) {
      colors = await concurrentMap(pool, 6, async function (pj) {
        return pj ? await fetchColorCached(pj.handle) : '';
      });
    }
    var wantType = normType(ctx.type);
    var groups = [[], [], [], []]; // exact+colour, exact, near+colour, near
    pool.forEach(function (pj, i) {
      if (!pj) return;
      var near = normType(pj.type) === wantType ? 0 : 2;
      var otherColor = ctx.color && colors[i] === ctx.color ? 0 : 1;
      groups[near + otherColor].push(pj);
    });
    var enriched = [];
    var seen = new Set();
    groups.forEach(function (group) {
      if (enriched.length >= count || !group.length) return;
      buildEnriched(group, count - enriched.length, ctx).forEach(function (e) {
        if (seen.has(e.productJson.handle)) return;
        seen.add(e.productJson.handle);
        enriched.push(e);
      });
    });
    if (!enriched.length) {
      var recs = await poolFromRecommendations(ctx.id, ctx.handle);
      enriched = buildEnriched(recs, count, ctx);
    }
    return enriched;
  }

  /* Fill in whatever the caller didn't provide (id / type / colour) from
     the registry, then from the product itself. */
  async function resolveContext(opts) {
    var handle = opts.handle;
    collectRegistryTags();
    var reg = registry[handle] || {};
    var ctx = {
      handle: handle,
      id: opts.id || reg.id || null,
      type: opts.type || reg.type || '',
      color: normColor(opts.color || reg.color || ''),
      size: opts.size || '',
      suggestions: opts.suggestions || reg.suggestions || [],
      fromMetafield: false
    };
    ctx.fromMetafield = !!(ctx.suggestions && ctx.suggestions.length);
    if (!ctx.id || !ctx.type) {
      try {
        var pj = await fetchProductJsonCached(handle);
        ctx.id = ctx.id || pj.id;
        ctx.type = ctx.type || pj.type;
      } catch (e) {}
    }
    if (!ctx.color) ctx.color = await fetchColorCached(handle);
    return ctx;
  }

  /* ── Cart ────────────────────────────────────────────────────────── */
  function openCartDrawer(opener) {
    if (window.PrysmCart && typeof window.PrysmCart.open === 'function') {
      try { window.PrysmCart.open(); } catch (e) {}
    }
    var events = [
      'dispatch:cart-drawer:open',
      'dispatch:cart-drawer:refresh',
      'cart:open',
      'cart:refresh',
      'cc:cart:open',
      'cc:cart:refresh',
      'theme:cart:open',
      'theme:cart:refresh'
    ];
    events.forEach(function (name) {
      try {
        document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: { opener: opener, source: 'xsell' } }));
      } catch (e) {}
    });
    var togglers = document.querySelectorAll(
      '[data-cart-drawer-open],[data-cc-cart-toggle],[data-drawer-toggle="cart"],button[name="open-cart"],.js-cart-toggle'
    );
    for (var i = 0; i < togglers.length; i++) {
      try {
        togglers[i].dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
        break;
      } catch (e) {}
    }
  }

  /* ── Modal ───────────────────────────────────────────────────────── */
  var modal = null;
  var boxEl = null;
  var trackEl = null;
  var headingEl = null;
  var announceEl = null;
  var renderSeq = 0;
  var currentCtx = null;

  function ensureModal() {
    if (modal && document.body.contains(modal)) return modal;
    modal = document.createElement('div');
    modal.className = 'xsell-modal';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-label', HEADING);
    modal.hidden = true;
    modal.innerHTML =
      '<div class="xsell-modal__overlay" data-xsell-close></div>' +
      '<div class="xsell-modal__box">' +
      '<button type="button" class="xsell-modal__close" aria-label="Close" data-xsell-close>' +
      '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">' +
      '<line x1="1" y1="1" x2="15" y2="15" stroke="currentColor" stroke-width="1.2" />' +
      '<line x1="15" y1="1" x2="1" y2="15" stroke="currentColor" stroke-width="1.2" />' +
      '</svg></button>' +
      '<label class="label xsell-onsoldout__heading"></label>' +
      '<div class="xsell-onsoldout__track" role="list"></div>' +
      '<p class="xsell-onsoldout__announce" role="status" aria-live="polite" hidden></p>' +
      '</div>';
    document.body.appendChild(modal);
    boxEl = modal.querySelector('.xsell-modal__box');
    trackEl = modal.querySelector('.xsell-onsoldout__track');
    headingEl = modal.querySelector('.xsell-onsoldout__heading');
    announceEl = modal.querySelector('.xsell-onsoldout__announce');
    bindModal();
    return modal;
  }

  /* Scroll lock via a dedicated class (not inline overflow): other
     components clear inline styles on their own teardown, which would let
     the page scroll behind our sheet. This `!important` class survives it. */
  function lockScroll() { document.documentElement.classList.add('xsell-lock'); }
  function unlockScroll() { document.documentElement.classList.remove('xsell-lock'); }

  function openModal() {
    ensureModal();
    modal.hidden = false;
    // Clear any inline transform/opacity left by a previous swipe-to-close
    // so the CSS enter animation starts clean.
    if (boxEl) {
      boxEl.style.transition = '';
      boxEl.style.transform = '';
      boxEl.style.opacity = '';
    }
    void modal.offsetWidth; // force reflow so the enter transition runs
    modal.classList.add('is-open');
    lockScroll();
  }
  function closeModal() {
    if (!modal) return;
    modal.classList.remove('is-open');
    unlockScroll();
    setTimeout(function () {
      if (modal && !modal.classList.contains('is-open')) modal.hidden = true;
    }, 400);
  }

  function bindModal() {
    // Close interactions: overlay / X button (+ Escape). On touch we handle
    // `touchend` with preventDefault so the browser never synthesises the
    // trailing `click` — otherwise that emulated click lands on whatever is
    // behind the sheet once it closes (ghost-click passthrough).
    var onCloseTap = function (e) {
      if (!(e.target.closest && e.target.closest('[data-xsell-close]'))) return;
      if (e.cancelable) e.preventDefault();
      e.stopPropagation();
      closeModal();
    };
    modal.addEventListener('touchend', onCloseTap, { passive: false });
    modal.addEventListener('click', onCloseTap);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && modal && !modal.hidden) closeModal();
    });

    // Mobile: swipe the sheet down to close. The drag only starts when the
    // grid isn't scrolled, so scrolling products and dismissing the sheet
    // don't fight each other.
    var startY = 0;
    var curY = 0;
    var dragging = false;
    var sheetH = 0;
    boxEl.addEventListener(
      'touchstart',
      function (e) {
        if (isDesktop()) return;
        if (e.target.closest('[data-xsell-close]')) return;
        if (trackEl && trackEl.contains(e.target) && trackEl.scrollTop > 0) return;
        startY = e.touches[0].clientY;
        curY = startY;
        dragging = true;
        sheetH = boxEl.offsetHeight;
        boxEl.style.transition = 'none';
      },
      { passive: true }
    );
    boxEl.addEventListener(
      'touchmove',
      function (e) {
        if (!dragging) return;
        curY = e.touches[0].clientY;
        var diff = curY - startY;
        if (diff > 0) {
          boxEl.style.transform = 'translateY(' + diff + 'px)';
          boxEl.style.opacity = Math.max(0, 1 - diff / sheetH);
        }
      },
      { passive: true }
    );
    boxEl.addEventListener(
      'touchend',
      function () {
        if (!dragging) return;
        dragging = false;
        var diff = curY - startY;
        if (diff > sheetH * 0.25) {
          boxEl.style.transition = 'transform 0.2s ease-out, opacity 0.2s ease-out';
          boxEl.style.transform = 'translateY(' + sheetH + 'px)';
          boxEl.style.opacity = '0';
          setTimeout(closeModal, 200);
          setTimeout(function () {
            boxEl.style.transition = '';
            boxEl.style.transform = '';
            boxEl.style.opacity = '';
          }, 300);
        } else {
          boxEl.style.transition = 'transform 0.2s ease-out, opacity 0.2s ease-out';
          boxEl.style.transform = 'translateY(0)';
          boxEl.style.opacity = '1';
          setTimeout(function () {
            boxEl.style.transition = '';
            boxEl.style.transform = '';
            boxEl.style.opacity = '';
          }, 200);
        }
        startY = 0;
        curY = 0;
      },
      { passive: true }
    );
  }

  /* Limit the grid to ~2 rows tall (and never past the viewport); the rest
     scrolls. Uses offsetTop (layout value, unaffected by the open transform). */
  function capModalRows(cols) {
    if (!trackEl) return;
    var items = trackEl.querySelectorAll('.xsell__item');
    trackEl.style.maxHeight = '';
    trackEl.style.overflowY = '';
    if (!items.length) return;
    var vhCap = Infinity;
    if (boxEl) {
      var cs = getComputedStyle(boxEl);
      var padV = (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0);
      var headH = headingEl
        ? headingEl.offsetHeight + (parseFloat(getComputedStyle(headingEl).marginBottom) || 0)
        : 0;
      var budget = isDesktop() ? window.innerHeight - 64 : window.innerHeight * 0.85;
      vhCap = budget - padV - headH - 8;
    }
    var cap = vhCap;
    if (items.length > cols * 2) {
      // Cap at the BOTTOM of the 2nd row, not the top of the 3rd row minus a
      // guessed gap — otherwise a wrong gap clips the last card's "Add to cart".
      var lastOfTwoRows = items[cols * 2 - 1] || items[items.length - 1];
      var twoRows = lastOfTwoRows.offsetTop - items[0].offsetTop + lastOfTwoRows.offsetHeight + 4;
      if (twoRows < cap) cap = twoRows;
    }
    if (isFinite(cap)) {
      trackEl.style.maxHeight = Math.max(cap, 140) + 'px';
      trackEl.style.overflowY = 'auto';
      trackEl.style.overflowX = 'hidden';
    }
  }

  function announce(text, ms) {
    if (!announceEl) return;
    announceEl.textContent = text;
    announceEl.hidden = false;
    setTimeout(function () { announceEl.hidden = true; }, ms || 1200);
  }

  function renderCards(enriched) {
    trackEl.innerHTML = '';
    var frag = document.createDocumentFragment();
    enriched.forEach(function (entry) {
      var pj = entry.productJson;
      var v = entry.matchedVariant;
      var url = pj.url || shopRoot() + 'products/' + pj.handle;
      var swatchUrl = USE_SWATCH ? findSwatchImageUrl(pj) : null;
      var imageSrc = swatchUrl || (pj.images && pj.images[0]) || '';
      var img = imageSrc ? '<img loading="lazy" src="' + imageSrc + '" alt="' + esc(pj.title) + '">' : '';
      var parts = splitTitleParts(pj.title);
      var priceHtml = SHOW_PRICE && v ? money(v.price) : '';
      var card = document.createElement('article');
      card.className = 'xsell__item';
      card.setAttribute('role', 'listitem');
      card.innerHTML =
        '<a class="xsell__image-link" href="' + url + '"><div class="xsell__image">' + img + '</div></a>' +
        '<div class="xsell__meta">' +
        '<a class="xsell__title-link" href="' + url + '">' +
        '<strong class="xsell__title-main">' + esc(parts.main) + '</strong>' +
        (parts.sub ? '<p class="xsell__title-sub">' + esc(parts.sub) + '</p>' : '') +
        '</a>' +
        '<div class="xsell__price">' + priceHtml + '</div>' +
        '<a href="#" class="xsell__addlink" role="button" aria-disabled="false" data-handle="' + pj.handle + '"' +
        (v ? ' data-vid="' + v.id + '"' : '') + '>' +
        '<img class="xsell__icon" src="' + ADD_ICON_URL + '" alt="" aria-hidden="true"/>' +
        esc(ADD_LABEL) +
        '</a>' +
        '</div>';
      frag.appendChild(card);
    });
    trackEl.appendChild(frag);
    trackEl.querySelectorAll('.xsell__item').forEach(function (card, i) {
      card.style.animationDelay = i * 60 + 'ms';
      card.classList.add('xsell--reveal');
    });
    trackEl.querySelectorAll('.xsell__addlink').forEach(function (link) {
      link.addEventListener('click', async function (e) {
        e.preventDefault();
        if (link.getAttribute('aria-disabled') === 'true') return;
        link.setAttribute('aria-disabled', 'true');
        try {
          var directVid = link.dataset.vid;
          var variantId = directVid ? parseInt(directVid, 10) : null;
          if (!variantId) {
            var handle = link.dataset.handle;
            var pj = productCache.get(handle) || (await fetchProductJsonCached(handle));
            var chosen = pickVariantForSizeStrict(pj, currentCtx && currentCtx.size);
            if (!chosen) throw new Error('No matching variant in stock for this size.');
            variantId = chosen.id;
          }
          var addRes = await fetch(shopRoot() + 'cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
          });
          if (!addRes.ok) {
            var err = await addRes.json().catch(function () { return {}; });
            throw new Error(err && err.description ? err.description : ERROR_TEXT);
          }
          announce(SUCCESS_TEXT, 1200);
          openCartDrawer(link);
        } catch (err) {
          announce(err && err.message ? err.message : ERROR_TEXT, 3000);
        } finally {
          link.setAttribute('aria-disabled', 'false');
        }
      });
    });
  }

  function setHeading(base, size, count) {
    if (!headingEl) return;
    headingEl.textContent = base;
    if (count == null) return;
    var info = '';
    if (size) info += ' · ' + size;
    info += ' (' + count + ')';
    var infoEl = document.createElement('span');
    infoEl.className = 'xsell-modal__heading-info';
    infoEl.textContent = info;
    headingEl.appendChild(infoEl);
  }

  async function open(opts) {
    opts = opts || {};
    if (!opts.handle) return;
    var seq = ++renderSeq;
    var base = opts.heading || HEADING;

    openModal();
    // Placeholder while the cascade resolves, so the box doesn't collapse.
    trackEl.style.maxHeight = '';
    trackEl.style.overflowY = '';
    trackEl.style.display = 'block';
    trackEl.innerHTML = '<p class="xsell-modal__loading">Loading…</p>';
    setHeading(base, null, null);

    var ctx = await resolveContext(opts);
    if (seq !== renderSeq) return;
    currentCtx = ctx;

    // Fetch as many as allowed; the grid shows ~2 rows and scrolls for the rest.
    var enriched = await buildCascade(ctx, Math.min(MAX_ITEMS, 20));
    if (seq !== renderSeq) return;

    // Columns match how many products there are (so the box wraps tightly and
    // there's no empty trailing slot): up to 5 on desktop, 2 on mobile.
    var maxCols = isDesktop() ? 5 : 2;
    var cols = Math.max(1, Math.min(enriched.length || 1, maxCols));
    trackEl.style.setProperty('--xsell-cols', cols);
    trackEl.classList.toggle('xsell--single', enriched.length === 1);
    setHeading(base, ctx.size, enriched.length);

    if (enriched.length) {
      trackEl.style.display = '';
      renderCards(enriched);
      capModalRows(cols);
    } else {
      trackEl.style.maxHeight = '';
      trackEl.style.overflowY = '';
      trackEl.style.display = 'block';
      trackEl.innerHTML =
        '<p class="xsell-modal__empty">' +
        (ctx.size ? 'No similar products available in this size.' : 'No similar products available.') +
        '</p>';
    }
  }

  /* Warm the caches before the shopper asks for the popup, so it opens with
     the products already in hand. Worth calling on a PDP that has sold-out
     sizes; not worth it per card on a listing. */
  async function prefetch(opts) {
    if (!opts || !opts.handle) return;
    try {
      var ctx = await resolveContext(opts);
      await poolFromType(ctx.type, ctx.handle);
    } catch (e) {}
  }

  /* Same cascade, no size and no modal: returns plain product data for a
     surface that renders its own list (the discontinued-product PDP, see
     snippets/sold-out-similar.liquid). Only products with stock in some
     size are returned. */
  async function similar(opts) {
    opts = opts || {};
    if (!opts.handle) return [];
    var ctx = await resolveContext(Object.assign({}, opts, { size: '' }));
    ctx.requireAvailable = true;
    var enriched = await buildCascade(ctx, opts.limit || 6);
    return enriched.map(function (entry) {
      var pj = entry.productJson;
      var v = entry.matchedVariant;
      var parts = splitTitleParts(pj.title);
      var compareAt = v && v.compare_at_price > v.price ? v.compare_at_price : null;
      return {
        handle: pj.handle,
        url: pj.url || shopRoot() + 'products/' + pj.handle,
        title: pj.title,
        titleMain: parts.main,
        titleSub: parts.sub,
        image: pj.featured_image || (pj.images && pj.images[0]) || '',
        price: v ? money(v.price) : '',
        compareAtPrice: compareAt ? money(compareAt) : ''
      };
    });
  }

  /* ── Declarative triggers ────────────────────────────────────────── */
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest && e.target.closest('[data-xsim-open]');
    if (!trigger) return;
    var handle = trigger.getAttribute('data-xsim-handle');
    if (!handle) return;
    e.preventDefault();
    e.stopPropagation();
    open({
      handle: handle,
      size: trigger.getAttribute('data-xsim-size') || '',
      heading: trigger.getAttribute('data-xsim-heading') || ''
    });
  });

  function registerMap(map) {
    if (!map) return;
    Object.keys(map).forEach(function (h) {
      var entry = map[h] || {};
      registry[h] = {
        id: entry.id || null,
        type: entry.type || '',
        color: normColor(entry.color || ''),
        suggestions: entry.suggestions || []
      };
    });
  }

  collectRegistryTags();

  window.LaagamXsell = {
    open: open,
    close: closeModal,
    prefetch: prefetch,
    similar: similar,
    register: registerMap
  };
})();
